import React from 'react';
import { Box, Typography, Grid, useMediaQuery, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useNavigateWithScrollMemory } from '../../../../hooks/useNavigateWithScrollMemory';
import { useRelatedAlbums } from '../../hooks/useRelatedAlbums';
import ProgressBar from '../../../../components/common/ProgressBar';
import theme from '../../../../theme';
import AlbumVerticalCard from '../../../../components/card/AlbumVerticalCard';

const LG_DISPLAY_COUNT = 6;
const MD_DISPLAY_COUNT = 4;
const XS_DISPLAY_COUNT = 3;

const RelatedAlbumsSection = ({ albumInfo }) => {
  const muiTheme = useTheme();
  const isLgUp = useMediaQuery(muiTheme.breakpoints.up('lg'));
  const isMdUp = useMediaQuery(muiTheme.breakpoints.up('md'));
  const { data: relatedAlbums = [], isLoading } = useRelatedAlbums(
    albumInfo?.sourceId,
    albumInfo?.albumSource,
  );
  const location = useLocation();
  const navigateWithScrollMemory = useNavigateWithScrollMemory();

  const displayCount = isLgUp ? LG_DISPLAY_COUNT : isMdUp ? MD_DISPLAY_COUNT : XS_DISPLAY_COUNT;
  const moreUrl = `${location.pathname}/related-albums${location.search}`;

  const isXs = useMediaQuery(theme.breakpoints.down('md'));

  if (!isLoading && relatedAlbums.length === 0) return null;

  return (
    <ProgressBar isLoading={isLoading}>
      {relatedAlbums.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* 헤더 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pb: 1,
              borderBottom: `1px solid ${theme.palette.grey.line}`,
            }}
          >
            <Typography fontSize={{ xs: 16, md: 18 }} fontWeight={700} color={theme.palette.black[9]}>
              좋아할 만한 다른 앨범
            </Typography>
            {relatedAlbums.length > displayCount && (
              <Typography
                component="button"
                type="button"
                onClick={() =>
                  navigateWithScrollMemory(moreUrl, {
                    state: {
                      artistName: albumInfo.artist?.name,
                      albumTitle: albumInfo.title,
                    },
                  })
                }
                sx={{
                  appearance: 'none',
                  border: 0,
                  background: 'transparent',
                  p: 0,
                  color: theme.palette.black[4],
                  cursor: 'pointer',
                  fontSize: { xs: 13, md: 14 },
                  fontWeight: 400,
                  lineHeight: 1.5,
                  whiteSpace: 'nowrap',
                }}
              >
                더보기
              </Typography>
            )}
          </Box>

          {/* 앨범 그리드 */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '30px',
            }}
          >
            <Box sx={{ overflow: 'hidden', width: '100%' }}>
              <Grid container rowSpacing={'30px'} columnSpacing={1}>
                {relatedAlbums.slice(0, displayCount).map((album) => (
                  <Grid item xs={4} md={3} lg={4} key={album.sourceId}>
                    <AlbumVerticalCard
                      albumId={album.albumId}
                      sourceId={album.sourceId}
                      albumSource={album.albumSource}
                      slug={album.slug}
                      imageUrl={album.albumImageUrl}
                      albumName={album.title}
                      artistId={album.artist?.sourceId}
                      artistDbId={album.artist?.id}
                      artistSource={album.artist?.artistSource}
                      artistName={album.artist?.name}
                      artistSlug={album.artist?.slug}
                      contentRating={album.contentRating}
                      albumAvgRating={album.averageRating ?? 0}
                      commentCount={album.commentCount ?? 0}
                      bookmarkCount={album.bookmarkCount ?? 0}
                      releaseDate={album.releaseDate}
                      isStarred={album.isStarred}
                      ratingSolo
                      dateSolo={isXs}
                      compact
                      referrerSection="recommended_album"
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </Box>
      )}
    </ProgressBar>
  );
};

export default RelatedAlbumsSection;
